/* ============================================================================
   /portal/auth/forgot-password - enter email, always shows success message.
   ----------------------------------------------------------------------------
   Reskinned to match the marketing welcome panel: hero gradient + neon green
   Space Grotesk headline. Behaviour preserved: always succeeds, even if no
   account exists, so we never leak which emails are registered.
   ========================================================================= */
import { useState } from "react";
import { Link } from "wouter";
import { useAuth } from "@/components/AuthProvider";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { ChevronRight, MailCheck } from "lucide-react";

export default function AuthForgotPassword() {
  const { forgotPassword } = useAuth();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    await forgotPassword(email.trim().toLowerCase());
    setSent(true);
    setBusy(false);
  };

  return (
    <div
      className="relative min-h-screen text-foreground flex justify-center px-4 auth-shell-safe-top pb-12 sm:items-center"
      style={{ background: "hsl(230 35% 4%)" }}
    >
      {/* Aurora glows */}
      <div
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          top: "-80px",
          right: "-60px",
          width: 360,
          height: 360,
          background: "radial-gradient(circle, hsl(142 78% 56% / 0.18), transparent 60%)",
        }}
      />
      <div
        aria-hidden
        className="absolute pointer-events-none"
        style={{
          bottom: "-60px",
          left: "-80px",
          width: 320,
          height: 320,
          background: "radial-gradient(circle, hsl(174 60% 53% / 0.10), transparent 60%)",
        }}
      />

      <div
        className="relative w-full max-w-sm v2-neon-card p-7"
        data-testid="card-forgot-password"
      >
        <div className="flex justify-center mb-5">
          <Logo size={48} />
        </div>

        {sent ? (
          <div className="text-center">
            <div
              className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{
                background: "hsl(142 78% 28% / 0.4)",
                border: "1.5px solid hsl(142 78% 56% / 0.55)",
                boxShadow: "0 0 24px hsl(142 100% 50% / 0.3)",
              }}
            >
              <MailCheck className="w-6 h-6" style={{ color: "hsl(142 78% 70%)" }} />
            </div>
            <div className="flex justify-center">
              <span className="v2-eyebrow" data-testid="badge-forgot-sent-eyebrow">
                <span className="v2-eyebrow__dot" />
                Check inbox
              </span>
            </div>
            <h1
              className="v2-display mt-4 text-2xl leading-tight"
              style={{ color: "var(--text-ink-bright)" }}
            >
              Link sent.
            </h1>
            <p
              className="text-xs mt-3 leading-relaxed"
              style={{ color: "var(--text-ink-faint)" }}
            >
              If an account exists for{" "}
              <span
                className="font-mono"
                style={{ color: "var(--text-ink-bright-soft)" }}
              >
                {email}
              </span>
              , we sent a reset link. Check your spam folder if it doesn't
              arrive within a few minutes.
            </p>
            <Link
              href="/login"
              className="v2-neon-cta mt-6 w-full inline-flex items-center justify-center gap-2 py-3 text-sm rounded-full no-underline"
              data-testid="button-back-portal"
            >
              Back to sign in
              <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        ) : (
          <>
            <div className="flex justify-center">
              <span className="v2-eyebrow" data-testid="badge-forgot-eyebrow">
                <span className="v2-eyebrow__dot" />
                Reset
              </span>
            </div>
            <h1
              className="v2-display mt-4 text-2xl text-center leading-tight"
              style={{ color: "var(--text-ink-bright)" }}
            >
              Forgot your password?{" "}
              <span style={{ color: "hsl(var(--primary))" }}>No worries.</span>
            </h1>
            <p
              className="text-xs text-center mt-2 mb-5 leading-relaxed"
              style={{ color: "var(--text-ink-faint)" }}
            >
              Enter your email and we will send a reset link if an account
              exists.
            </p>
            <form onSubmit={submit} className="space-y-3.5">
              <div>
                <Label
                  htmlFor="forgot-email"
                  className="text-[11px] font-semibold uppercase tracking-wider"
                  style={{ color: "var(--text-ink-faint)" }}
                >
                  Email
                </Label>
                <Input
                  id="forgot-email"
                  data-testid="input-forgot-email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1.5 focus-visible:ring-2"
                  style={{
                    background: "hsl(230 25% 9%)",
                    border: "1px solid hsl(142 50% 25%)",
                    color: "var(--text-ink-bright)",
                  }}
                />
              </div>
              <button
                type="submit"
                disabled={busy}
                data-testid="button-send-reset"
                className="v2-neon-cta w-full inline-flex items-center justify-center gap-2 py-3 text-sm rounded-full disabled:opacity-60"
              >
                {busy ? "Sending..." : "Send reset link"}
                {!busy && <ChevronRight className="w-4 h-4" />}
              </button>
            </form>
            <p
              className="text-xs mt-5 text-center"
              style={{ color: "hsl(142 18% 60%)" }}
            >
              <Link
                href="/login"
                className="no-underline"
                style={{ color: "hsl(142 18% 80%)" }}
                data-testid="link-back"
              >
                Back to sign in
              </Link>
            </p>
          </>
        )}
      </div>
    </div>
  );
}
